import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollY, direction } = useScrollAnimation();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    
    if (element) {
      // Close menu immediately
      setIsMenuOpen(false);
      
      // Add a small delay to ensure menu closes before scrolling
      setTimeout(() => {
        const headerHeight = 80; // Approximate header height
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  const menuItems = [
    { id: 'hero', label: 'Inicio' },
    { id: 'our-story', label: 'Nuestra historia' },
    { id: 'event-details', label: 'Evento' },
    { id: 'rsvp', label: 'Asistencia' },
    //{ id: 'spotify-playlist', label: 'Playlist' },
    { id: 'gallery', label: 'Galería' }
  ];

  // Header background changes based on scroll
  const headerBg = scrollY > 100 
    ? 'bg-cream-50/95 backdrop-blur-md shadow-lg border-b border-sand-200' 
    : 'bg-cream-50/80 backdrop-blur-sm';

  // Hide header when scrolling down, show when scrolling up
  const headerTransform = direction === 'down' && scrollY > 200 ? 'translateY(-100%)' : 'translateY(0)';

  return (
    <motion.header
      style={{ 
        transform: headerTransform,
        transition: 'transform 0.3s ease-in-out'
      }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      <nav className="container-max section-padding py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="cursor-pointer"
            onClick={() => scrollToSection('hero')}
          >
            <div className="flex items-center space-x-3">
              <div>
                <h1 className="font-wasted text-2xl font-normal text-wedding-primary">
                  Ale y Loren
                </h1>
                <p className="font-avenir text-xs text-olive-700 font-medium">17 octubre 2026</p>
              </div>
            </div>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8">
            {menuItems.map((item, index) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                whileHover={{ 
                  scale: 1.05,
                  color: '#504B38' // forest-950
                }}
                className="font-avenir text-olive-700 hover:text-forest-950 transition-colors duration-200 font-medium relative group"
              >
                {item.label}
                <motion.div
                  className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-olive-500 to-forest-700 origin-left"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mobile-menu-button md:hidden p-3 rounded-xl bg-gradient-to-br from-sand-100 to-olive-100 text-wedding-primary hover:from-sand-200 hover:to-olive-200 transition-all duration-200 cursor-pointer"
            type="button"
            aria-label="Toggle mobile menu"
          >
            <motion.svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              animate={{ rotate: isMenuOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </motion.svg>
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          initial={false}
          animate={{ 
            opacity: isMenuOpen ? 1 : 0,
            scaleY: isMenuOpen ? 1 : 0,
          }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className={`md:hidden origin-top ${isMenuOpen ? 'block' : 'hidden'}`}
        >
          <div className="py-4 mt-4 border-t border-sand-300">
            <div className="grid grid-cols-2 gap-3">
              {menuItems.map((item, index) => (
                <motion.button
                  key={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    scrollToSection(item.id);
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mobile-menu-item text-left py-3 px-4 rounded-xl bg-gradient-to-r from-cream-100 to-sand-100 text-wedding-secondary hover:from-cream-200 hover:to-sand-200 transition-all duration-200 border border-sand-200 cursor-pointer"
                >
                  <span className="font-avenir font-medium pointer-events-none">{item.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </nav>
    </motion.header>
  );
};

export default Header; 
